// Copyright (c) 2026 SoundDrift. All rights reserved.

import React, { useState } from 'react';
import AudioAudition from './AudioAudition.jsx';
import Waveform from '../Waveform.jsx';
import { HeadphonesAffordance } from '../DesignComponents.jsx';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

export default function SoundPicker({
  hand = [],
  maxPicks = 2,
  selectedClips = [],
  onSelectClip = () => {},
  onConfirmSelection = () => {},
  voicyEnabled = false,
  onGenerateSFX = null,
  onSearchOpenverse = null,
  onSearchVoicy = null,
  disabled = false,
  showSlotCards = true,
  showHeadphones = true,
  className = '',
  style = {},
}) {
  const [activeTab, setActiveTab] = useState('hand'); // 'hand', 'openverse', 'generative', 'voicy'
  const [searchQuery, setSearchQuery] = useState('');
  const [genPrompt, setGenPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [genNotice, setGenNotice] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Filter hand by search query
  const filteredHand = hand.filter(clip => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (clip.label && clip.label.toLowerCase().includes(q)) ||
           (clip.category && clip.category.toLowerCase().includes(q));
  });

  const handlePickClip = (clip) => {
    onSelectClip(clip);
  };

  const handleOpenverseSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !onSearchOpenverse) return;
    setIsSearching(true);
    try {
      const res = await onSearchOpenverse(searchQuery);
      setSearchResults(res.clips || []);
    } catch {
      // Search failed silently
    } finally {
      setIsSearching(false);
    }
  };

  const handleVoicySearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !onSearchVoicy) return;
    setIsSearching(true);
    try {
      const res = await onSearchVoicy(searchQuery);
      setSearchResults(res.clips || []);
    } catch {
      // Search failed silently
    } finally {
      setIsSearching(false);
    }
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    if (!genPrompt.trim() || !onGenerateSFX || isGenerating) return;

    setIsGenerating(true);
    setGenNotice(null);

    try {
      const res = await onGenerateSFX(genPrompt);
      if (res) {
        if (res.degraded) {
          setGenNotice(`Budget limit reached (${res.reason}). Used curated library sound.`);
        } else if (res.cacheHit) {
          setGenNotice('Instant cache hit ($0.00 cost)');
        } else {
          setGenNotice('Audio generated via fal.ai ElevenLabs SFX');
        }
        // Auto-select generated clip if returned
        onSelectClip(res);
      }
    } catch (err) {
      setGenNotice(`Generation failed: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const isSelected = (clipId) => selectedClips.some(c => c.id === clipId);

  const isPickComplete = selectedClips.length === maxPicks;

  const tabStyle = (tabId) => ({
    minHeight: '44px',
    minWidth: '44px',
    padding: '0 14px',
    borderRadius: '8px',
    background: activeTab === tabId ? '#171C22' : 'transparent',
    border: activeTab === tabId ? '1px solid #1C2229' : '1px solid transparent',
    color: activeTab === tabId ? '#EDEFF2' : '#79838D',
    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    cursor: 'pointer',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  });

  const renderClipRow = (clip) => {
    const isPicked = isSelected(clip.id);
    const selectedIndex = selectedClips.findIndex(c => c.id === clip.id);
    const slotTag = selectedIndex >= 0 ? (selectedIndex === 0 ? 'A' : (selectedIndex === 1 ? 'B' : String.fromCharCode(65 + selectedIndex))) : null;
    const durationText = clip.dur || (typeof clip.duration === 'number' ? `${clip.duration.toFixed(1)}s` : '2.4s');

    return (
      <div
        key={clip.id}
        data-testid={`clip-row-${clip.id}`}
        className={`clip-row ${isPicked ? 'selected' : ''}`}
        onClick={() => !disabled && handlePickClip(clip)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '68px',
          minHeight: '68px',
          padding: '0 12px 0 10px',
          borderRadius: '12px',
          border: `1px solid ${isPicked ? (clip.color || '#3ACFCF') : '#1C2229'}`,
          background: isPicked ? 'rgba(255, 255, 255, 0.028)' : '#0E1116',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        {/* Audition pad (AudioAudition component) */}
        <div
          style={{ flex: 'none', width: '44px', height: '44px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <AudioAudition
            clip={clip}
            compact
            disabled={disabled}
            showAttribution={false}
            style={{
              width: '44px',
              height: '44px',
              minWidth: '44px',
              minHeight: '44px',
              padding: 0,
              margin: 0,
              border: 'none',
              background: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          />
        </div>

        {/* Clip details & Waveform */}
        <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', minWidth: 0 }}>
            <span
              title={formatClipTitle(clip, 100)}
              style={{
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.005em',
                color: isPicked ? '#EDEFF2' : '#C0C8D0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: '1 1 0',
              }}
            >
              {formatClipTitle(clip)}
            </span>
            <span
              style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '10.5px',
                color: '#6E7982',
                flex: 'none',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {durationText}
            </span>
          </div>

          {/* Waveform envelope */}
          <Waveform
            peaks={clip.peaks || null}
            seed={clip.id || clip.label}
            bars={36}
            height={24}
            color={isPicked ? (clip.color || '#3ACFCF') : (clip.color || '#3ACFCF')}
            dimColor={isPicked ? '#39424A' : '#262D34'}
            showPlayhead={false}
            ariaLabel={`Waveform of ${clip.label || 'clip'}`}
          />
        </div>

        {/* Slot tag indicator (A, B) */}
        <div
          style={{
            width: '20px',
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isPicked && (
            <span
              data-testid={`slot-tag-${clip.id}`}
              className="slot-tag"
              style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '11px',
                fontWeight: 600,
                color: clip.color || '#3ACFCF',
              }}
            >
              {slotTag}
            </span>
          )}
        </div>
      </div>
    );
  };

  const inputStyle = {
    width: '100%',
    minHeight: '44px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: '1px solid #1C2229',
    background: '#0E1116',
    color: '#EDEFF2',
    fontSize: '14px',
    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnStyle = (variant = 'default') => ({
    minHeight: '44px',
    minWidth: '44px',
    padding: '0 18px',
    borderRadius: '10px',
    background: variant === 'accent' ? '#3ACFCF' : '#171C22',
    border: variant === 'accent' ? '1px solid #3ACFCF' : '1px solid #1C2229',
    color: variant === 'accent' ? '#08090B' : '#C6CDD4',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
  });

  return (
    <div
      className={`sound-picker-container ${className}`}
      data-testid="sound-picker"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        maxWidth: '600px',
        margin: '0 auto',
        ...style
      }}
    >
      {showHeadphones && <HeadphonesAffordance visible={true} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', borderBottom: '1px solid #1A1F25', paddingBottom: '8px', overflowX: 'auto' }}>
        <button type="button" onClick={() => setActiveTab('hand')} data-testid="tab-hand" style={tabStyle('hand')}>
          Hand ({hand.length})
        </button>
        <button type="button" onClick={() => setActiveTab('openverse')} data-testid="tab-openverse" style={tabStyle('openverse')}>
          Openverse CC
        </button>
        <button type="button" onClick={() => setActiveTab('generative')} data-testid="tab-generative" style={tabStyle('generative')}>
          AI Generate
        </button>
        {voicyEnabled && (
          <button type="button" onClick={() => setActiveTab('voicy')} data-testid="tab-voicy" style={tabStyle('voicy')}>
            Voicy
          </button>
        )}
      </div>

      {/* Selected Clips Summary — slot cards from artboard */}
      {showSlotCards && (
        <div style={{
          display: 'flex',
          gap: '10px',
        }}>
          {Array.from({ length: maxPicks }, (_, i) => {
            const clip = selectedClips[i];
            return (
              <div
                key={i}
                onClick={() => clip && onSelectClip(clip)}
                style={{
                  flex: '1 1 0',
                  height: '76px',
                  borderRadius: '12px',
                  border: clip ? `1px solid ${clip.color || '#3ACFCF'}` : '1px dashed #20262D',
                  background: clip ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '5px',
                  padding: '0 12px',
                  cursor: clip ? 'pointer' : 'default',
                  boxSizing: 'border-box',
                }}
              >
                {clip ? (
                  <>
                    <Waveform
                      peaks={clip.peaks || null}
                      seed={clip.id || clip.label}
                      bars={34}
                      height={26}
                      color={clip.color || '#3ACFCF'}
                      showPlayhead={false}
                    />
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', minWidth: 0 }}>
                      <span
                        title={formatClipTitle(clip, 100)}
                        style={{
                          fontSize: '11.5px',
                          fontWeight: 600,
                          color: '#C6CDD4',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: '1 1 0',
                        }}
                      >
                        {formatClipTitle(clip)}
                      </span>
                      <span style={{
                        fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                        fontSize: '10px',
                        color: '#6E7982',
                        flexShrink: 0,
                      }}>
                        {clip.dur || (typeof clip.duration === 'number' ? `${clip.duration.toFixed(1)}s` : '')}
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    fontSize: '11px',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#6E7982',
                  }}>
                    {i === 0 ? 'Sound A' : 'Sound B'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Count label + confirm CTA */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          fontSize: '10.5px',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#6E7982',
        }}>
          Your library
        </span>
        <span style={{
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          fontSize: '10.5px',
          letterSpacing: '0.06em',
          color: '#79838D',
        }}>
          {selectedClips.length} / {maxPicks} chosen
        </span>
      </div>

      {/* Tab 1: Hand / Curated Library */}
      {activeTab === 'hand' && (
        <div>
          <input
            placeholder="Search hand by label or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ ...inputStyle, marginBottom: '10px' }}
            data-testid="hand-search-input"
          />

          <div className="hand-clips-list" data-testid="clip-hand-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredHand.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#79838D' }}>
                No matching clips found in hand.
              </div>
            ) : (
              filteredHand.map(clip => renderClipRow(clip))
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Openverse Search */}
      {activeTab === 'openverse' && (
        <div>
          <form onSubmit={handleOpenverseSearch} style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <input
              placeholder="Search Openverse (e.g. thunder, footsteps)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={inputStyle}
              data-testid="openverse-search-input"
            />
            <button type="submit" disabled={isSearching} style={btnStyle()} data-testid="openverse-search-btn">
              {isSearching ? '...' : 'Search'}
            </button>
          </form>

          <div style={{
            fontSize: '12px',
            color: '#79838D',
            marginBottom: '10px',
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            letterSpacing: '0.04em',
          }}>
            Creative Commons licensed audio. Attribution rendered automatically.
          </div>

          <div className="openverse-clips-list" data-testid="openverse-clips-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {searchResults.map(clip => renderClipRow(clip))}
          </div>
        </div>
      )}

      {/* Tab 3: AI Generative SFX */}
      {activeTab === 'generative' && (
        <div>
          <div style={{
            background: '#0D1015',
            border: '1px solid #1C2229',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
          }}>
            <div style={{
              fontWeight: 700,
              color: '#EDEFF2',
              fontSize: '14px',
              marginBottom: '6px',
            }}>
              ElevenLabs SFX v2 (via fal.ai)
            </div>
            <div style={{
              fontSize: '12px',
              color: '#79838D',
              marginBottom: '12px',
              lineHeight: 1.5,
            }}>
              Describe a sound effect to generate it. Cached sounds are instant and free.
            </div>

            <form onSubmit={handleGenerateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                placeholder="e.g., cartoon spring boing jump"
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                style={inputStyle}
                data-testid="generative-prompt-input"
              />
              <button
                type="submit"
                disabled={isGenerating || !genPrompt.trim()}
                style={{
                  ...btnStyle('accent'),
                  opacity: (isGenerating || !genPrompt.trim()) ? 0.5 : 1,
                }}
                data-testid="generate-sfx-btn"
              >
                {isGenerating ? 'Generating...' : 'Generate Sound'}
              </button>
            </form>

            <div style={{
              fontSize: '11px',
              color: '#5A646D',
              marginTop: '10px',
              fontStyle: 'italic',
            }}>
              Generated sounds join the public SoundDrift library catalog.
            </div>
          </div>

          {genNotice && (
            <div
              data-testid="generative-notice"
              style={{
                padding: '10px 14px',
                borderRadius: '10px',
                background: '#0D1015',
                border: '1px solid #1C2229',
                color: '#3ACFCF',
                fontSize: '12px',
                marginBottom: '12px',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}
            >
              {genNotice}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Voicy Search */}
      {activeTab === 'voicy' && (
        <div>
          {!voicyEnabled ? (
            <div style={{ textAlign: 'center', padding: '24px', color: '#79838D' }}>
              Voicy provider is currently disabled by default.
            </div>
          ) : (
            <div>
              <form onSubmit={handleVoicySearch} style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                  placeholder="Search Voicy memes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={inputStyle}
                />
                <button type="submit" disabled={isSearching} style={btnStyle()}>
                  Search
                </button>
              </form>

              <div className="voicy-clips-list" data-testid="voicy-clips-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {searchResults.map(clip => renderClipRow(clip))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom CTA */}
      <div style={{ flex: 'none', padding: '14px 0 0', borderTop: '1px solid #1A1F25' }}>
        <button
          type="button"
          onClick={onConfirmSelection}
          disabled={!isPickComplete || disabled}
          data-testid="confirm-selection-btn"
          style={{
            width: '100%',
            height: '56px',
            borderRadius: '14px',
            background: isPickComplete ? '#3ACFCF' : '#0E1116',
            border: isPickComplete ? '1px solid #3ACFCF' : '1px solid #1C2229',
            color: isPickComplete ? '#08090B' : '#6E7982',
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '-0.005em',
            cursor: (isPickComplete && !disabled) ? 'pointer' : 'default',
            transition: 'all 0.15s ease',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}
        >
          {isPickComplete ? 'Send both sounds' : `Pick ${maxPicks - selectedClips.length} more`}
        </button>
        <div style={{
          textAlign: 'center',
          marginTop: '11px',
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          fontSize: '10.5px',
          letterSpacing: '0.08em',
          color: '#6E7982',
        }}>
          Tap the pad to audition privately
        </div>
      </div>
    </div>
  );
}
